import { create } from 'zustand'

// Mock data for development
const mockTickets = [
  {
    id: 'TKT-001',
    title: 'Cannot reset password',
    description: 'I\'ve tried resetting my password but keep getting an error message.',
    status: 'open',
    priority: 'high',
    channel: 'sms',
    customer: { name: 'John Smith', email: 'john@example.com', phone: '410-555-1234' },
    assignedTo: { id: '2', name: 'Sarah Johnson' },
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 30 * 60 * 1000),
    messages: [
      { role: 'customer', text: 'Cannot reset password', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000) },
      { role: 'ai', text: 'I\'ll help you reset your password. Have you checked your spam folder?', timestamp: new Date(Date.now() - 1.5 * 60 * 60 * 1000) },
    ],
    resolution: null,
    tags: ['password', 'urgent'],
  },
  {
    id: 'TKT-002',
    title: 'Training course not loading',
    description: 'Module 3 of the Python course won\'t load on mobile.',
    status: 'in_progress',
    priority: 'medium',
    channel: 'chat',
    customer: { name: 'Maria Garcia', email: 'maria@example.com', phone: '410-555-5678' },
    assignedTo: { id: '3', name: 'Mike Chen' },
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
    messages: [],
    resolution: null,
    tags: ['training', 'technical'],
  },
  {
    id: 'TKT-003',
    title: 'Billing inquiry',
    description: 'Question about subscription renewal on my account.',
    status: 'resolved',
    priority: 'low',
    channel: 'email',
    customer: { name: 'Robert Wilson', email: 'robert@example.com', phone: '410-555-9012' },
    assignedTo: { id: '4', name: 'Lisa Brown' },
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    messages: [],
    resolution: 'Clarified billing cycle and renewed subscription',
    tags: ['billing'],
  },
]

export const useTicketStore = create((set, get) => ({
  tickets: mockTickets,
  selectedTicket: null,
  loading: false,
  filters: {
    status: 'all',
    priority: 'all',
    assignedTo: 'all',
    channel: 'all',
  },

  // Fetch all tickets
  fetchTickets: async () => {
    set({ loading: true })
    try {
      // Integration point: Replace with actual API call
      // const response = await apiClient.get('/api/v1/tickets')
      // Simulating API delay
      await new Promise(resolve => setTimeout(resolve, 500))
      set({ loading: false })
    } catch (error) {
      set({ loading: false })
      throw error
    }
  },

  // Get ticket by ID
  getTicket: (ticketId) => {
    const { tickets } = get()
    return tickets.find(t => t.id === ticketId)
  },

  // Select a ticket
  selectTicket: (ticketId) => {
    const ticket = get().getTicket(ticketId)
    set({ selectedTicket: ticket })
  },

  // Create new ticket
  createTicket: async (data) => {
    set({ loading: true })
    try {
      // Integration point: Replace with actual API call
      // const response = await apiClient.post('/api/v1/tickets', data)
      const newTicket = {
        id: `TKT-${String(get().tickets.length + 1).padStart(3, '0')}`,
        ...data,
        createdAt: new Date(),
        updatedAt: new Date(),
        messages: [],
        resolution: null,
      }
      set((state) => ({
        tickets: [newTicket, ...state.tickets],
        loading: false,
      }))
      return newTicket
    } catch (error) {
      set({ loading: false })
      throw error
    }
  },

  // Update ticket
  updateTicket: async (ticketId, updates) => {
    set({ loading: true })
    try {
      // Integration point: Replace with actual API call
      // await apiClient.patch(`/api/v1/tickets/${ticketId}`, updates)
      set((state) => ({
        tickets: state.tickets.map(t =>
          t.id === ticketId ? { ...t, ...updates, updatedAt: new Date() } : t
        ),
        selectedTicket:
          state.selectedTicket?.id === ticketId
            ? { ...state.selectedTicket, ...updates, updatedAt: new Date() }
            : state.selectedTicket,
        loading: false,
      }))
    } catch (error) {
      set({ loading: false })
      throw error
    }
  },

  // Add message to ticket
  addMessage: async (ticketId, message) => {
    set({ loading: true })
    try {
      // Integration point: Replace with actual API call
      // await apiClient.post(`/api/v1/tickets/${ticketId}/messages`, { text: message })
      const newMessage = {
        role: 'agent',
        text: message,
        timestamp: new Date(),
      }
      set((state) => ({
        tickets: state.tickets.map(t =>
          t.id === ticketId
            ? {
                ...t,
                messages: [...t.messages, newMessage],
                updatedAt: new Date(),
              }
            : t
        ),
        selectedTicket:
          state.selectedTicket?.id === ticketId
            ? {
                ...state.selectedTicket,
                messages: [...state.selectedTicket.messages, newMessage],
                updatedAt: new Date(),
              }
            : state.selectedTicket,
        loading: false,
      }))
    } catch (error) {
      set({ loading: false })
      throw error
    }
  },

  // Set filters
  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }))
  },

  // Get filtered tickets
  getFilteredTickets: () => {
    const { tickets, filters } = get()
    return tickets.filter(ticket => {
      if (filters.status !== 'all' && ticket.status !== filters.status) return false
      if (filters.priority !== 'all' && ticket.priority !== filters.priority) return false
      if (filters.channel !== 'all' && ticket.channel !== filters.channel) return false
      if (filters.assignedTo !== 'all' && ticket.assignedTo?.id !== filters.assignedTo) return false
      return true
    })
  },

  // Get statistics
  getStats: () => {
    const { tickets } = get()
    return {
      total: tickets.length,
      open: tickets.filter(t => t.status === 'open').length,
      inProgress: tickets.filter(t => t.status === 'in_progress').length,
      resolved: tickets.filter(t => t.status === 'resolved').length,
      highPriority: tickets.filter(t => t.priority === 'high' && t.status !== 'resolved').length,
      avgResponseTime: '2 hours',
      aiResolutionRate: 72,
    }
  },
}))
